import ErrorBoundary from 'containers/error_boundary'
import isElectron from 'is-electron'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  LocalAudioTrack,
  LocalParticipant,
  LocalTrackOptions,
  LocalTrackPublication,
  LocalVideoTrack,
  createLocalAudioTrack as createLocalAudioTrackTwilio,
  createLocalVideoTrack as createLocalVideoTrackTwilio,
} from 'twilio-video'
import { Room } from 'twilio-video'

import { useCallManager } from '../../../contexts/call_manager_context'
import usePreviousProps from '../../../hooks/previous_props_hook'
import { polyfillGetDisplayMedia } from '../../../util/get_display_media_polyfill'
import { CS } from '../call_manager/connection_status_reducer'
import {
  CAMERA_VIDEO_NAME,
  SCREEN_SHARE_VIDEO_NAME,
  getCamOptions,
  getScreenShareOptions,
} from '../util/web_rtc_config'
import { useAudioTrack } from './use_audio_track'
import { useIsTrackEnabled } from './use_is_track_enabled'
import { useManageSelectedDevices } from './use_manage_selected_devices'
import { useVideoTrack } from './use_video_track'

export const SELECTED_AUDIO_DEVICE = 'topic__selected__audio__device'
export const SELECTED_VIDEO_DEVICE = 'topic__selected__video__device'
export const SELECTED_AUDIO_OUTPUT_DEVICE =
  'topic__selected__audio__output__device'
const SHARE_AUDIO_ERROR = 'Error sharing your audio, please try again'
const SHARE_VIDEO_ERROR = 'Error sharing your video, please try again'
const SHARE_SCREEN_ERROR = 'Error sharing your screen, please try again'

type HandleDeviceChange = (
  deviceId: string | null,
) => Promise<LocalTrackPublication | void>

export type LocalTrackData = {
  audioDevices: { [id: string]: MediaDeviceInfo }
  videoDevices: { [id: string]: MediaDeviceInfo }
  audioOutputDevices: { [id: string]: MediaDeviceInfo }
  hasAudioDevices: boolean
  hasVideoDevices: boolean
  hasAudioOutputDevices: boolean
  isAudioEnabled: boolean
  isVideoEnabled: boolean
  isScreenShareEnabled: boolean
  isScreenShareSupported: boolean
  toggleAudio: () => Promise<string | void>
  toggleVideo: () => Promise<string | void>
  toggleScreenShare: () => Promise<string | void>
  audioDeviceId: string | null
  videoDeviceId: string | null
  audioOutputDeviceId: string | null
  handleAudioDeviceChange: HandleDeviceChange
  handleVideoDeviceChange: HandleDeviceChange
  handleAudioOutputDeviceChange: HandleDeviceChange
  publishedAudioTrack: LocalAudioTrack | undefined
  publishedVideoTrack: LocalVideoTrack | undefined
  publishedScreenShareTrack: LocalVideoTrack | undefined
  unpublishedAudioTrack?: LocalAudioTrack | null
  unpublishedVideoTrack?: LocalVideoTrack | null
  unpublishedScreenShareTrack?: LocalVideoTrack | null
  createLocalAudioTrack: any
  createLocalVideoTrack: any
  setUnpublishedAudioTrack?: React.Dispatch<
    React.SetStateAction<LocalAudioTrack | null>
  >
  setUnpublishedVideoTrack?: React.Dispatch<
    React.SetStateAction<LocalVideoTrack | null>
  >
  setVideoDeviceId: React.Dispatch<null | string>
}

export const useLocalTracks = (
  room?: Room | null,
  participant?: LocalParticipant | undefined,
): LocalTrackData => {
  const {
    audioDevices,
    videoDevices,
    audioOutputDevices,
    hasAudioDevices,
    hasVideoDevices,
    hasAudioOutputDevices,
    audioDeviceId,
    videoDeviceId,
    audioOutputDeviceId,
    setAudioDeviceId,
    setVideoDeviceId,
    setAudioOutputDeviceId,
  } = useManageSelectedDevices()

  const [wasVideoDisabledToScreenShare, setWasVideoDisabledToScreenShare] =
    useState<boolean>(false)

  const [unpublishedAudioTrack, setUnpublishedAudioTrack] =
    useState<LocalAudioTrack | null>(null)
  const publishedAudioTrack = useAudioTrack(participant) as LocalAudioTrack
  const audioTrack = publishedAudioTrack || unpublishedAudioTrack
  const isAudioEnabled = useIsTrackEnabled(audioTrack)

  const [unpublishedVideoTrack, setUnpublishedVideoTrack] =
    useState<LocalVideoTrack | null>(null)
  const publishedVideoTrack = useVideoTrack(participant) as LocalVideoTrack
  const videoTrack = publishedVideoTrack || unpublishedVideoTrack
  const isVideoEnabled = useIsTrackEnabled(videoTrack)
  const prevIsVideoEnabled = usePreviousProps(isVideoEnabled)

  const [unpublishedScreenShareTrack, setUnpublishedScreenShareTrack] =
    useState<LocalVideoTrack | null>(null)
  const publishedScreenShareTrack = useVideoTrack(
    participant,
    SCREEN_SHARE_VIDEO_NAME,
  ) as LocalVideoTrack
  const screenShareTrack =
    publishedScreenShareTrack || unpublishedScreenShareTrack
  const isScreenShareEnabled = useIsTrackEnabled(screenShareTrack)
  const prevIsScreenShareEnabled = usePreviousProps(isScreenShareEnabled)

  const [changingMicrophone, setChangingMicrophone] = useState(false)
  const [changingCamera, setChangingCamera] = useState(false)

  const {
    activeCall: { connectionStatus, prevConnectionStatus, navToVideoView },
  } = useCallManager()

  // stop all tracks before window unload
  useEffect(() => {
    const cleanup = () => {
      unpublishedAudioTrack?.stop()
      unpublishedVideoTrack?.stop()
      unpublishedScreenShareTrack?.stop()
      setUnpublishedAudioTrack(null)
      setUnpublishedVideoTrack(null)
      setUnpublishedScreenShareTrack(null)
      publishedAudioTrack?.stop()
      publishedVideoTrack?.stop()
      publishedScreenShareTrack?.stop()
    }
    window.addEventListener('beforeunload', cleanup)

    return () => {
      window.removeEventListener('beforeunload', cleanup)
    }
  }, [
    publishedAudioTrack,
    publishedScreenShareTrack,
    publishedVideoTrack,
    unpublishedAudioTrack,
    unpublishedScreenShareTrack,
    unpublishedVideoTrack,
  ])

  /* when the room is joined the unpublished tracks are published. This useEffect nullifies the unpublished tracks
  in reaction to the room being joined. One thing to note is the Participant component also has detach logic, which is
  important for detaching the unpublished track before attaching the published track. This is because the Participant
  component is a child meaning its useEffect will run first. The detach logic here is still needed as the Participant
  component is not always rendered, e.g. when on "chat view" in the app */
  useEffect(() => {
    if (
      prevConnectionStatus === CS.JOINING &&
      connectionStatus === CS.ROOM_CONNECTED
    ) {
      if (unpublishedAudioTrack) {
        setUnpublishedAudioTrack(null)
      }

      if (unpublishedVideoTrack) {
        unpublishedVideoTrack.detach()
        setUnpublishedVideoTrack(null)
      }

      if (unpublishedScreenShareTrack) {
        unpublishedScreenShareTrack.detach()
        setUnpublishedScreenShareTrack(null)
      }
    }
  }, [
    connectionStatus,
    prevConnectionStatus,
    room,
    unpublishedAudioTrack,
    unpublishedScreenShareTrack,
    unpublishedVideoTrack,
  ])

  // if the room closes, but the person remains connected to the call,
  // reassign the published tracks as unpublished tracks
  useEffect(() => {
    if (
      (prevConnectionStatus === CS.ROOM_CONNECTED ||
        prevConnectionStatus === CS.ROOM_RECONNECTING) &&
      connectionStatus === CS.CALL_CONNECTED
    ) {
      if (participant) {
        // unpublish tracks. Do not stop the tracks, because the room closing does not mean the person left the call
        participant.audioTracks.forEach((publication) => {
          publication.unpublish()
        })
        participant.videoTracks.forEach((publication) => {
          publication.unpublish()
        })
      }

      if (publishedAudioTrack) {
        setUnpublishedAudioTrack(publishedAudioTrack as LocalAudioTrack)
      }
      if (publishedVideoTrack) {
        setUnpublishedVideoTrack(publishedVideoTrack as LocalVideoTrack)
      }
      if (publishedScreenShareTrack) {
        setUnpublishedScreenShareTrack(
          publishedScreenShareTrack as LocalVideoTrack,
        )
      }
    }
  }, [
    connectionStatus,
    participant,
    prevConnectionStatus,
    publishedAudioTrack,
    publishedScreenShareTrack,
    publishedVideoTrack,
  ])

  // if the person leaves the call, stop (and nullify) all tracks
  // unpublishing of tracks is handled elsewhere in reaction to the room disconnecting
  useEffect(() => {
    if (
      prevConnectionStatus !== CS.DISCONNECTED &&
      connectionStatus === CS.DISCONNECTED
    ) {
      if (unpublishedAudioTrack) {
        unpublishedAudioTrack.stop()
        setUnpublishedAudioTrack(null)
      }
      if (unpublishedVideoTrack) {
        unpublishedVideoTrack.stop()
        setUnpublishedVideoTrack(null)
      }
      if (unpublishedScreenShareTrack) {
        unpublishedScreenShareTrack.stop()
        setUnpublishedScreenShareTrack(null)
      }
    }
  }, [
    connectionStatus,
    prevConnectionStatus,
    unpublishedAudioTrack,
    unpublishedScreenShareTrack,
    unpublishedVideoTrack,
  ])

  // handle track stop on unpublished audio track e.g. device disconnected
  useEffect(() => {
    if (unpublishedAudioTrack) {
      const onStopped = () => {
        setUnpublishedAudioTrack(null)
      }
      unpublishedAudioTrack.on('stopped', onStopped)

      return () => {
        unpublishedAudioTrack.off('stopped', onStopped)
      }
    }
  }, [unpublishedAudioTrack])

  // handle track stop on unpublished video track e.g. device disconnected
  useEffect(() => {
    if (unpublishedVideoTrack) {
      const onStopped = (track: LocalVideoTrack) => {
        setUnpublishedVideoTrack(null)
        track.detach()
      }
      unpublishedVideoTrack.on('stopped', onStopped)

      return () => {
        unpublishedVideoTrack.off('stopped', onStopped)
      }
    }
  }, [unpublishedVideoTrack])

  // handle track stop on unpublished screen share track e.g. stop sharing via browser ui button
  useEffect(() => {
    if (unpublishedScreenShareTrack) {
      const onStopped = (track: LocalVideoTrack) => {
        setUnpublishedScreenShareTrack(null)
        track.detach()
      }
      unpublishedScreenShareTrack.on('stopped', onStopped)

      return () => {
        unpublishedScreenShareTrack.off('stopped', onStopped)
      }
    }
  }, [unpublishedScreenShareTrack])

  useEffect(() => {
    if (
      (!prevIsVideoEnabled && isVideoEnabled) ||
      (!prevIsScreenShareEnabled && isScreenShareEnabled)
    ) {
      navToVideoView && navToVideoView()
    }
  }, [
    isVideoEnabled,
    prevIsVideoEnabled,
    navToVideoView,
    prevIsScreenShareEnabled,
    isScreenShareEnabled,
  ])

  const createLocalAudioTrack = useCallback(
    async (deviceId = undefined) => {
      const id = deviceId === undefined ? audioDeviceId : deviceId
      return createLocalAudioTrackTwilio({
        ...(id ? { deviceId: { exact: id } } : {}),
      }).then(
        (track) => {
          setAudioDeviceId(id)
          return Promise.resolve(track)
        },
        (e) => {
          ErrorBoundary.logErrorToMyService(e)
          return Promise.reject(SHARE_AUDIO_ERROR)
        },
      )
    },
    [audioDeviceId, setAudioDeviceId],
  )

  const turnAudioOn = useCallback(() => {
    if (audioTrack) {
      audioTrack.enable()
      return Promise.resolve()
    } else {
      return createLocalAudioTrack().then((track) => {
        // can create local tracks before the webrtc room is connected, so return early if no local participant
        if (!participant) {
          setUnpublishedAudioTrack(track)
          return Promise.resolve()
        }

        return participant.publishTrack(track).then(
          () => {
            return Promise.resolve()
          },
          (e) => {
            ErrorBoundary.logErrorToMyService(e)
            return Promise.reject(SHARE_AUDIO_ERROR)
          },
        )
      })
    }
  }, [audioTrack, createLocalAudioTrack, participant])

  const turnAudioOff = useCallback(() => {
    audioTrack?.disable()
    return Promise.resolve()
  }, [audioTrack])

  const createLocalVideoTrack = useCallback(
    async (deviceId = undefined) => {
      const id = deviceId === undefined ? videoDeviceId : deviceId
      const options = {
        ...getCamOptions(),
        ...(id ? { deviceId: { exact: id } } : {}),
      } as unknown as LocalTrackOptions

      return createLocalVideoTrackTwilio(options).then(
        (track: LocalVideoTrack) => {
          setVideoDeviceId(id)
          return Promise.resolve(track)
        },
        (e) => {
          ErrorBoundary.logErrorToMyService(e)
          return Promise.reject(SHARE_VIDEO_ERROR)
        },
      )
    },
    [setVideoDeviceId, videoDeviceId],
  )

  const turnVideoCameraOn = useCallback(() => {
    return createLocalVideoTrack().then((track: LocalVideoTrack) => {
      if (!participant) {
        setUnpublishedVideoTrack(track)
        return Promise.resolve()
      }

      return participant.publishTrack(track).then(
        () => {
          return Promise.resolve()
        },
        (e) => {
          ErrorBoundary.logErrorToMyService(e)
          return Promise.reject(SHARE_VIDEO_ERROR)
        },
      )
    })
  }, [createLocalVideoTrack, participant])

  const turnVideoCameraOff = useCallback(() => {
    if (unpublishedVideoTrack) {
      unpublishedVideoTrack.stop()
      setUnpublishedVideoTrack(null)
      return Promise.resolve()
    } else {
      if (participant) {
        const publications = participant.videoTracks
        publications.forEach((publication) => {
          if (publication.track.name.startsWith(CAMERA_VIDEO_NAME)) {
            publication.track.stop()
            publication.unpublish()
          }
        })
        return Promise.resolve()
      } else {
        const errorMessage = 'Failed to turn video off, please try again'
        ErrorBoundary.logErrorToMyService(new Error(errorMessage))
        return Promise.reject(errorMessage)
      }
    }
  }, [unpublishedVideoTrack, participant])

  useEffect(() => {
    let previousGetDisplayMedia: any = undefined

    if (isElectron()) {
      previousGetDisplayMedia = window?.navigator?.mediaDevices?.getDisplayMedia
      polyfillGetDisplayMedia()
    }

    return () => {
      if (previousGetDisplayMedia && window?.navigator?.mediaDevices) {
        window.navigator.mediaDevices.getDisplayMedia = previousGetDisplayMedia
      }
    }
  }, [])

  const turnScreenShareOn = useCallback(() => {
    const videoOptions = getScreenShareOptions()

    return navigator.mediaDevices.getDisplayMedia({ video: videoOptions }).then(
      (stream: any) => {
        if (!stream) {
          return Promise.resolve()
        }

        const streamTrack = stream.getVideoTracks()[0]
        // eslint-disable-next-line
        // @ts-ignore
        const track = new LocalVideoTrack(streamTrack, {
          name: SCREEN_SHARE_VIDEO_NAME,
        })
        if (!participant) {
          setUnpublishedScreenShareTrack(track)
          return Promise.resolve()
        }

        participant
          .publishTrack(track, {
            priority: 'high',
          })
          .catch((e) => {
            ErrorBoundary.logErrorToMyService(e)
            return Promise.reject(SHARE_SCREEN_ERROR)
          })
      },
      (e: any) => {
        ErrorBoundary.logErrorToMyService(e)
        let errorMessage
        if (e.name === 'NotAllowedError') {
          errorMessage = 'Error sharing screen, please check permissions'
        } else {
          errorMessage = SHARE_SCREEN_ERROR
        }
        return Promise.reject(errorMessage)
      },
    )
  }, [participant])

  const turnScreenShareOff = useCallback(() => {
    if (unpublishedScreenShareTrack) {
      unpublishedScreenShareTrack.stop()
      setUnpublishedScreenShareTrack(null)
      return Promise.resolve()
    } else {
      if (participant) {
        const publications = participant.videoTracks
        publications.forEach((publication) => {
          if (publication.track.name === SCREEN_SHARE_VIDEO_NAME) {
            publication.track.stop()
            publication.unpublish()
          }
        })
        return Promise.resolve()
      } else {
        const errorMessage = 'Failed to turn screenshare off. Please try again.'
        ErrorBoundary.logErrorToMyService(new Error(errorMessage))
        return Promise.reject(errorMessage)
      }
    }
  }, [participant, unpublishedScreenShareTrack])

  const toggleAudio = useCallback(() => {
    return isAudioEnabled ? turnAudioOff() : turnAudioOn()
  }, [isAudioEnabled, turnAudioOff, turnAudioOn])

  const toggleVideo = useCallback(() => {
    if (!isVideoEnabled && isScreenShareEnabled) {
      return turnScreenShareOff().then(() => {
        setWasVideoDisabledToScreenShare(false)
        return turnVideoCameraOn()
      })
    } else {
      return isVideoEnabled ? turnVideoCameraOff() : turnVideoCameraOn()
    }
  }, [
    isScreenShareEnabled,
    isVideoEnabled,
    turnScreenShareOff,
    turnVideoCameraOff,
    turnVideoCameraOn,
  ])

  const toggleScreenShare = useCallback(() => {
    if (!isScreenShareEnabled && isVideoEnabled) {
      return turnVideoCameraOff().then(() => {
        setWasVideoDisabledToScreenShare(true)
        return turnScreenShareOn()
      })
    } else if (wasVideoDisabledToScreenShare && isScreenShareEnabled) {
      return turnScreenShareOff().then(() => {
        setWasVideoDisabledToScreenShare(false)
        return turnVideoCameraOn()
      })
    } else {
      return isScreenShareEnabled ? turnScreenShareOff() : turnScreenShareOn()
    }
  }, [
    isScreenShareEnabled,
    isVideoEnabled,
    turnScreenShareOff,
    turnScreenShareOn,
    turnVideoCameraOff,
    turnVideoCameraOn,
    wasVideoDisabledToScreenShare,
  ])

  const handleAudioDeviceChange: HandleDeviceChange = useCallback(
    (deviceId) => {
      if (changingMicrophone) {
        return Promise.reject(
          new Error('Failed to change microphone, please try again'),
        )
      }
      setChangingMicrophone(true)
      if (deviceId && deviceId === audioDeviceId) {
        setChangingMicrophone(false)
        return Promise.resolve()
      }
      if (unpublishedAudioTrack) {
        unpublishedAudioTrack.stop()
        unpublishedAudioTrack.detach()
        setUnpublishedAudioTrack(null)
      }

      if (participant) {
        const publications = participant.audioTracks
        publications.forEach((publication) => {
          publication.track.stop()
          publication.track.detach()
          publication.unpublish()
        })
      }

      setAudioDeviceId(deviceId)

      return createLocalAudioTrack(deviceId)
        .then(
          // eslint-disable-next-line
          // @ts-ignore
          (track: LocalAudioTrack) => {
            if (!isAudioEnabled) {
              track.disable()
            }

            if (!participant) {
              setUnpublishedAudioTrack(track)
              return Promise.resolve()
            }

            return participant.publishTrack(track).catch((e) => {
              ErrorBoundary.logErrorToMyService(e)
              return Promise.reject(SHARE_AUDIO_ERROR)
            })
          },
        )
        .finally(() => {
          setChangingMicrophone(false)
        })
    },
    [
      changingMicrophone,
      audioDeviceId,
      unpublishedAudioTrack,
      participant,
      setAudioDeviceId,
      createLocalAudioTrack,
      isAudioEnabled,
    ],
  )

  const handleVideoDeviceChange: HandleDeviceChange = useCallback(
    (deviceId) => {
      if (changingCamera) {
        return Promise.reject(
          new Error('Failed to change camera, please try again'),
        )
      }
      setChangingCamera(true)
      if (deviceId === videoDeviceId) {
        setChangingCamera(false)
        return Promise.resolve()
      }
      if (unpublishedVideoTrack) {
        unpublishedVideoTrack.stop()
        unpublishedVideoTrack.detach()
        setUnpublishedVideoTrack(null)
      }

      if (participant) {
        const publications = participant.videoTracks
        publications.forEach((publication) => {
          publication.track.stop()
          publication.track.detach()
          publication.unpublish()
        })
      }

      setVideoDeviceId(deviceId)

      if (isVideoEnabled) {
        return (
          createLocalVideoTrack(deviceId)
            // eslint-disable-next-line
            // @ts-ignore
            .then((track: LocalVideoTrack) => {
              if (!participant) {
                setUnpublishedVideoTrack(track)
                return Promise.resolve()
              }

              return participant.publishTrack(track).catch((e) => {
                ErrorBoundary.logErrorToMyService(e)
                return Promise.reject(SHARE_VIDEO_ERROR)
              })
            })
            .finally(() => {
              setChangingCamera(false)
            })
        )
      } else {
        setChangingCamera(false)
        return Promise.resolve()
      }
    },
    [
      changingCamera,
      createLocalVideoTrack,
      isVideoEnabled,
      participant,
      setVideoDeviceId,
      unpublishedVideoTrack,
      videoDeviceId,
    ],
  )

  const handleAudioOutputDeviceChange: HandleDeviceChange = useCallback(
    (deviceId) => {
      setAudioOutputDeviceId(deviceId)
      return Promise.resolve()
    },
    [setAudioOutputDeviceId],
  )

  return useMemo(() => {
    const isScreenShareSupported = !!(
      navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
    )
    const localTrackData: LocalTrackData = {
      toggleAudio,
      toggleVideo,
      toggleScreenShare,
      isAudioEnabled,
      isVideoEnabled,
      isScreenShareEnabled,
      isScreenShareSupported,
      audioDevices,
      videoDevices,
      audioOutputDevices,
      hasAudioDevices,
      hasVideoDevices,
      hasAudioOutputDevices,
      audioDeviceId,
      videoDeviceId,
      audioOutputDeviceId,
      handleAudioDeviceChange,
      handleVideoDeviceChange,
      handleAudioOutputDeviceChange,
      publishedAudioTrack,
      publishedVideoTrack,
      publishedScreenShareTrack,
      unpublishedAudioTrack,
      unpublishedVideoTrack,
      unpublishedScreenShareTrack,
      setUnpublishedAudioTrack,
      setUnpublishedVideoTrack,
      createLocalAudioTrack,
      createLocalVideoTrack,
      setVideoDeviceId,
    }

    return localTrackData
  }, [
    audioDeviceId,
    audioDevices,
    audioOutputDeviceId,
    audioOutputDevices,
    createLocalAudioTrack,
    createLocalVideoTrack,
    handleAudioDeviceChange,
    handleAudioOutputDeviceChange,
    handleVideoDeviceChange,
    hasAudioDevices,
    hasAudioOutputDevices,
    hasVideoDevices,
    isAudioEnabled,
    isScreenShareEnabled,
    isVideoEnabled,
    publishedAudioTrack,
    publishedScreenShareTrack,
    publishedVideoTrack,
    setVideoDeviceId,
    toggleAudio,
    toggleScreenShare,
    toggleVideo,
    unpublishedAudioTrack,
    unpublishedScreenShareTrack,
    unpublishedVideoTrack,
    videoDeviceId,
    videoDevices,
  ])
}
